import { useState, useEffect, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import type { Provider, UserIdentity } from '@supabase/supabase-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '@pantry/ui';
import { AnalyticsEvents } from '@pantry/analytics';
import { useAuth } from '../../contexts/useAuth';
import { useAnalytics } from '../../hooks/useAnalytics';
import { toast } from 'sonner';
import { LinkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { logger } from '../../lib/logger';

// Supported providers (hardcoded based on Supabase configuration)
interface SupportedProvider {
  id: Provider;
  name: string;
  enabled: boolean;
  comingSoon?: boolean;
}

const SUPPORTED_PROVIDERS: SupportedProvider[] = [
  { id: 'apple', name: 'Apple', enabled: true },
  { id: 'google', name: 'Google', enabled: true },
];

function getProviderIcon(provider: string) {
  switch (provider.toLowerCase()) {
    case 'apple':
      return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      );
    case 'google':
      return (
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      );
    case 'email':
      return (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
          />
        </svg>
      );
    default:
      return <LinkIcon className="h-5 w-5" />;
  }
}

function getProviderName(provider: string, t: ReturnType<typeof useLingui>['t']): string {
  switch (provider.toLowerCase()) {
    case 'apple':
      return t`Apple`;
    case 'google':
      return t`Google`;
    case 'email':
      return t`Email & Password`;
    default:
      return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
}

export function LinkedAccountsSection() {
  const { t } = useLingui();
  const { track } = useAnalytics();
  const { getLinkedIdentities, linkProvider, unlinkProvider } = useAuth();
  const [identities, setIdentities] = useState<UserIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadIdentities = useCallback(async () => {
    try {
      const data = await getLinkedIdentities();
      setIdentities(data);
    } catch (error) {
      logger.error('Failed to load identities', error);
    } finally {
      setIsLoading(false);
    }
  }, [getLinkedIdentities]);

  useEffect(() => {
    loadIdentities();
  }, [loadIdentities]);

  const handleLink = async (provider: Provider) => {
    setActionLoading(provider);
    try {
      await linkProvider(provider);
      // Note: linkProvider redirects to OAuth flow, so we won't reach this
      // Tracking happens after redirect callback completes
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to link account`;
      toast.error(message);
      setActionLoading(null);
    }
  };

  const handleUnlink = async (identity: UserIdentity) => {
    // Check if this is the last identity
    if (identities.length <= 1) {
      toast.error(t`You must have at least one sign-in method. Add another account first.`);
      return;
    }

    setActionLoading(identity.identity_id);
    try {
      await unlinkProvider(identity);
      await loadIdentities();
      track(AnalyticsEvents.ACCOUNT_UNLINKED, { provider: identity.provider });
      const providerName = getProviderName(identity.provider, t);
      toast.success(t`${providerName} account unlinked`);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to unlink account`;
      toast.error(message);
    } finally {
      setActionLoading(null);
    }
  };

  // Get linked provider IDs
  const linkedProviders = new Set(identities.map((i) => i.provider.toLowerCase()));

  // Available providers to link (not already linked and enabled)
  const availableToLink = SUPPORTED_PROVIDERS.filter(
    (p) => !linkedProviders.has(p.id.toLowerCase())
  );

  const canUnlink = identities.length > 1;

  return (
    <Card className="rounded-xl" data-tour="settings-linked-accounts">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <Trans>Linked Accounts</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>Manage connected accounts for signing in to TrackMyGroceries.</Trans>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 rounded-xl bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 rounded bg-muted" />
                  <div className="h-3 w-32 rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Connected Accounts */}
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Trans>Connected</Trans>
              </p>
              <div className="space-y-2">
                {identities.map((identity) => (
                  <div
                    key={identity.identity_id}
                    className="flex items-center gap-4 rounded-xl bg-muted/50 p-3"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-background">
                      {getProviderIcon(identity.provider)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">
                        {getProviderName(identity.provider, t)}
                      </p>
                      {identity.identity_data?.email && (
                        <p className="text-sm text-muted-foreground truncate">
                          {identity.identity_data.email as string}
                        </p>
                      )}
                    </div>
                    {canUnlink && identity.provider !== 'email' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlink(identity)}
                        disabled={actionLoading === identity.identity_id}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg"
                      >
                        {actionLoading === identity.identity_id ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-destructive/30 border-t-destructive" />
                        ) : (
                          <Trans>Unlink</Trans>
                        )}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">
                        <Trans>Primary</Trans>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Available to Link */}
            {availableToLink.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Trans>Link Another Account</Trans>
                </p>
                <div className="space-y-2">
                  {availableToLink.map((provider) => (
                    <div
                      key={provider.id}
                      className="flex items-center gap-4 rounded-xl bg-muted/50 p-3"
                    >
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-xl bg-background ${
                          provider.comingSoon ? 'opacity-50' : ''
                        }`}
                      >
                        {getProviderIcon(provider.id)}
                      </div>
                      <div className="flex-1">
                        <p
                          className={`font-medium ${provider.comingSoon ? 'text-muted-foreground' : 'text-foreground'}`}
                        >
                          {provider.name}
                        </p>
                      </div>
                      {provider.comingSoon ? (
                        <span className="text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2 py-1 rounded-md">
                          <Trans>Coming Soon</Trans>
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleLink(provider.id)}
                          disabled={actionLoading === provider.id}
                          className="rounded-lg"
                        >
                          {actionLoading === provider.id ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                          ) : (
                            <>
                              <PlusIcon className="h-4 w-4 mr-1" />
                              <Trans>Link</Trans>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info Note */}
            <p className="text-xs text-muted-foreground">
              <Trans>
                Linked accounts can be used to sign in to TrackMyGroceries. You must keep at least
                one sign-in method.
              </Trans>
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
